import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { changeBillingPlan } from '../../redux/features/authSlice';
import { getCurrentPackage } from '../../utils/packageLimits';

const NAME_TO_ID = { Free: 1, Starter: 2, Standard: 3, Business: 4, Premium: 5 };

const THEME = {
  gray: 'border-gray-600 bg-gray-800/80',
  green: 'border-green-700 bg-green-900/20',
  blue: 'border-blue-700 bg-blue-900/20',
  purple: 'border-purple-700 bg-purple-900/20',
  yellow: 'border-yellow-700 bg-yellow-900/20',
};

/**
 * Dno stranice paketa: izbor istog plana kao na dashboardu (API), ili demo localStorage.
 */
const PackagePlanFooter = ({ packageId, theme = 'gray' }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [pending, setPending] = useState(false);

  const isDemoUser = user?.result?.email === 'demo@gmail.com';
  const currentName = getCurrentPackage(user);
  const currentId = NAME_TO_ID[currentName] ?? Number(user?.result?.package) ?? 1;
  const isCurrent = currentId === packageId;

  const handleClick = async () => {
    if (isCurrent) {
      toast.info(t('packages.alreadyThisPlan', 'Ovo je već vaš trenutni paket.'));
      return;
    }

    if (isDemoUser) {
      const label = Object.keys(NAME_TO_ID).find((k) => NAME_TO_ID[k] === packageId) || 'Free';
      localStorage.setItem('demoPackage', label);
      toast.success(t('packages.demoPackageUpdated', 'Demo paket je ažuriran.'));
      window.dispatchEvent(new Event('spintasker-demo-package'));
      return;
    }

    setPending(true);
    try {
      await dispatch(changeBillingPlan(packageId)).unwrap();
      toast.success(t('packages.planUpdated', 'Paket je ažuriran.'));
    } catch (err) {
      const msg =
        typeof err === 'string'
          ? err
          : typeof err?.payload === 'string'
            ? err.payload
            : err?.message || err?.error || t('packages.planUpdateError', 'Greška pri izboru paketa.');
      toast.error(msg);
    } finally {
      setPending(false);
    }
  };

  const boxClass = THEME[theme] || THEME.gray;

  return (
    <div className={`rounded-xl p-6 border ${boxClass}`}>
      <h3 className="text-lg font-bold text-white mb-2">
        {t('packages.chooseThisPackageTitle', 'Izaberi ovaj paket')}
      </h3>
      <p className="text-sm text-gray-400 mb-4">
        {t(
          'packages.chooseThisPackageHint',
          'Svaki paket uključuje mesec dana probnog perioda. Nakon probe, obračun prati izabrani plan po kalendarskim danima u mesecu.'
        )}
      </p>
      <button
        type="button"
        disabled={!!pending}
        onClick={handleClick}
        className={`w-full sm:w-auto min-w-[200px] px-6 py-3 rounded-lg font-semibold transition flex items-center justify-center gap-2 ${
          isCurrent
            ? 'bg-gray-700 text-gray-300 cursor-default border border-gray-600'
            : 'bg-emerald-600 hover:bg-emerald-700 text-white'
        }`}
      >
        {pending ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            {t('common.saving', 'Čuvanje...')}
          </>
        ) : isCurrent ? (
          t('packages.currentPackageButton', 'Trenutni paket')
        ) : (
          t('packages.chooseThisPackageCta', 'Izaberi paket')
        )}
      </button>
    </div>
  );
};

export default PackagePlanFooter;
